// Reads widget configuration from data-* attributes on the injecting <script> tag.
//
// document.currentScript is only valid during the script's *synchronous* execution,
// so we capture it at module-eval time (which runs as soon as the bundle loads) —
// it stays valid even if we mount later, after DOMContentLoaded.

export interface WidgetConfig {
  /** Origin the widget calls for /chat and /escalate. Defaults to the script's origin. */
  apiBase: string;
  /** Brand accent color (any CSS color). */
  accent: string;
  /** Header title. */
  title: string;
  /** First assistant message shown on open (UI only — not sent to the model). */
  greeting: string;
  /** Composer placeholder. */
  placeholder: string;
  /** Quick-reply topic buttons shown before the first message. */
  quickReplies: string[];
  /** Optional Smartsupp key — lazy-loads Smartsupp if window.smartsupp is absent. */
  smartsuppKey: string;
}

const DEFAULTS = {
  accent: "#9C0707",
  title: "DTS Support",
  greeting:
    "Hello, and thank you for visiting our store! If you have any questions, " +
    "don't hesitate to ask me right here in chat. You can also tap a topic below to get started.",
  placeholder: "Type your question…",
  // "Shipping & delivery" must keep this exact text — qa.csv row 49 (the official
  // CS shipping-time answer) is keyed to it.
  // "Create an account" routes through the model's offer_account_signup tool, which
  // renders the in-chat signup form (added 2026-08-06 while the site's own signup
  // page is broken; kept as a convenience feature).
  quickReplies:
    "Track my order|Shipping & delivery|Ordering questions|Need a sample?|Account questions|Create an account|Bulk / wholesale pricing",
};

const SCRIPT_EL: HTMLScriptElement | null = (function () {
  const cs = document.currentScript as HTMLScriptElement | null;
  if (cs && cs.src) return cs;
  // Fallback: the last <script> on the page whose src points at the bundle.
  const list = document.querySelectorAll<HTMLScriptElement>('script[src*="widget.js"]');
  return list.length ? list[list.length - 1] : null;
})();

function attr(name: string): string | null {
  return SCRIPT_EL ? SCRIPT_EL.getAttribute(name) : null;
}

function splitList(raw: string): string[] {
  return raw
    .split("|")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function readConfig(): WidgetConfig {
  // api base: explicit attr wins; otherwise derive from the script's own origin so
  // the widget talks to whichever host served it.
  let apiBase = attr("data-api-base") || "";
  if (!apiBase && SCRIPT_EL && SCRIPT_EL.src) {
    try {
      apiBase = new URL(SCRIPT_EL.src).origin;
    } catch {
      apiBase = "";
    }
  }
  apiBase = apiBase.replace(/\/+$/, ""); // strip trailing slash(es)

  return {
    apiBase,
    accent: attr("data-accent") || DEFAULTS.accent,
    title: attr("data-title") || DEFAULTS.title,
    greeting: attr("data-greeting") || DEFAULTS.greeting,
    placeholder: attr("data-placeholder") || DEFAULTS.placeholder,
    quickReplies: splitList(attr("data-quick-replies") || DEFAULTS.quickReplies),
    smartsuppKey: attr("data-smartsupp-key") || "",
  };
}
